import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { useEndpoints } from '../../../hooks/useEndpoints';
import DateNavigator from '../DateNavigator';
import dayjs from 'dayjs';
import styles from './AthleteBodyMap.module.scss';
import {
  ZONE_LABELS,
  TYPE_LABELS,
  IMPACT_LABELS,
  INTENSITY_LEGEND,
  getIntensityColor,
} from '../../../shared/bodyMap/bodyMapScale';
import AnatomyFigure from '../../../shared/bodyMap/AnatomyFigure';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { PulseIcon } from '@phosphor-icons/react';

interface ComplaintItem {
  zone: string;
  intensity: number;
  type: 'pain' | 'stiffness' | 'swelling' | 'weakness';
  impact: 'can_do_all' | 'will_limit' | 'cannot_do_all';
  note?: string;
}

interface BodyMapDocument {
  _id: string;
  userId: string;
  date: string;
  complaints: ComplaintItem[];
}

// Zone muscolari con cap DOMS (intensità max 3): usato solo per la nota nel
// pannello di dettaglio (fonte soglie: @leopart/calc body-map).
const DOMS_ZONES = new Set([
  'quadricep_dx', 'quadricep_sx',
  'hamstring_dx', 'hamstring_sx',
  'calf_dx', 'calf_sx',
  'upper_back', 'lumbar',
]);

/** Range numerico da mostrare accanto alla label canonica di INTENSITY_LEGEND (stesso ordine: Lieve/Moderata/Intensa). */
const INTENSITY_RANGES = ['1-2', '3', '4-5'];

interface Props {
  athleteId: string;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
}

const AthleteBodyMap: FC<Props> = ({ athleteId, selectedDate: selectedDateProp, onDateChange }) => {
  const [internalDate, setInternalDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const selectedDate = selectedDateProp ?? internalDate;
  const setSelectedDate = onDateChange ?? setInternalDate;
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const endpoints = useEndpoints();

  const { data, isLoading, isError, refetch } = useQuery<BodyMapDocument[]>({
    queryKey: ['bodymap', 'athlete', athleteId, selectedDate],
    queryFn: async () => {
      const { data } = await endpoints.getBodyMapAthlete(athleteId, {
        startDate: selectedDate,
        endDate: selectedDate,
      });
      return data;
    },
    enabled: !!athleteId,
    staleTime: 1000 * 60 * 2,
  });

  const dayData = data?.[0] ?? null;
  const complaintsMap = new Map<string, ComplaintItem>(
    (dayData?.complaints ?? []).map((c) => [c.zone, c])
  );
  // Zona → intensità per colorare la figura anatomica (deriva da complaintsMap).
  const zoneIntensity = new Map<string, number>(
    Array.from(complaintsMap.entries()).map(([zone, c]) => [zone, c.intensity])
  );
  const hoveredComplaint = hoveredZone ? complaintsMap.get(hoveredZone) ?? null : null;
  const compiledCount = complaintsMap.size;

  const toggleZone = (zone: string) =>
    setHoveredZone((prev) => (prev === zone ? null : zone));

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.title}>Body Map Fastidi</span>
          {compiledCount > 0 && (
            <span className={styles.badge}>{compiledCount} zone</span>
          )}
        </div>
        <DateNavigator selectedDate={selectedDate} onDateChange={setSelectedDate} />
      </div>

      {isLoading ? (
        <div className={styles.stateWrap}>
          <LeopartLoader size="sm" />
        </div>
      ) : isError ? (
        <div className={styles.stateWrap}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : (
        <div className={styles.content}>
          {/* Figura anatomica */}
          <div className={styles.svgWrapper}>
            <div className={styles.svgCol}>
              <span className={styles.viewLabel}>Frontale</span>
              <AnatomyFigure
                view="front"
                zoneIntensity={zoneIntensity}
                hoveredZone={hoveredZone}
                onHoverZone={setHoveredZone}
                onSelectZone={toggleZone}
              />
            </div>
            <div className={styles.svgCol}>
              <span className={styles.viewLabel}>Posteriore</span>
              <AnatomyFigure
                view="back"
                zoneIntensity={zoneIntensity}
                hoveredZone={hoveredZone}
                onHoverZone={setHoveredZone}
                onSelectZone={toggleZone}
              />
            </div>

            {/* Info panel */}
            <div className={styles.infoPanel}>
              {hoveredZone ? (
                <>
                  <div className={styles.infoPanelZoneName}>
                    {ZONE_LABELS[hoveredZone] ?? hoveredZone}
                  </div>
                  {hoveredComplaint ? (
                    <div className={styles.infoPanelBody}>
                      <div className={styles.intensityRow}>
                        <span className={styles.intensityLabel}>Intensità</span>
                        <span
                          className={styles.intensityValue}
                          style={{ background: getIntensityColor(hoveredComplaint.intensity) }}
                        >
                          {hoveredComplaint.intensity} / 5
                        </span>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoKey}>Tipo</span>
                        <span className={styles.infoVal}>
                          {TYPE_LABELS[hoveredComplaint.type] ?? hoveredComplaint.type}
                        </span>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoKey}>Impatto</span>
                        <span className={styles.infoVal}>
                          {IMPACT_LABELS[hoveredComplaint.impact] ?? hoveredComplaint.impact}
                        </span>
                      </div>
                      {hoveredComplaint.note && (
                        <div className={styles.noteRow}>
                          <span className={styles.infoKey}>Note</span>
                          <span className={styles.noteVal}>{hoveredComplaint.note}</span>
                        </div>
                      )}
                      {DOMS_ZONES.has(hoveredZone) && (
                        <div className={styles.domsNote}>
                          Zona muscolare — intensità max 3 (DOMS)
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.infoPanelEmpty}>
                      Nessun fastidio segnalato per questa zona
                    </div>
                  )}
                </>
              ) : compiledCount > 0 ? (
                <>
                  <div className={styles.infoPanelHint}>
                    Tocca o passa il cursore su una zona colorata per i dettagli
                  </div>
                  <div className={styles.zoneList}>
                    {Array.from(complaintsMap.entries()).map(([zone, c]) => (
                      <div
                        key={zone}
                        className={styles.zoneListRow}
                        onMouseEnter={() => setHoveredZone(zone)}
                        onClick={() => toggleZone(zone)}
                      >
                        <span
                          className={styles.zoneListDot}
                          style={{ background: getIntensityColor(c.intensity) }}
                        />
                        <span className={styles.zoneListLabel}>{ZONE_LABELS[zone] ?? zone}</span>
                        <span className={styles.zoneListIntensity}>{c.intensity}/5</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={styles.infoPanelEmptyState}>
                  <PulseIcon
                    className={styles.infoPanelEmptyIcon}
                    size={30}
                    weight="regular"
                  />
                  <div className={styles.infoPanelEmptyTitle}>
                    Nessun fastidio per questa data
                  </div>
                  <div className={styles.infoPanelEmptyHint}>
                    Scegli un giorno segnalato nel calendario qui sotto, oppure
                    l'atleta può registrarlo dall'app.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Empty state */}
          {compiledCount === 0 && (
            <div className={styles.emptyState}>
              Nessun fastidio registrato per questa data
            </div>
          )}

          {/* Legenda */}
          <div className={styles.legend}>
            {INTENSITY_LEGEND.map((item, idx) => (
              <div key={item.label} className={styles.legendItem}>
                <span
                  className={cx(styles.legendDot, {
                    [styles.legendDotLieve]: idx === 0,
                    [styles.legendDotModerata]: idx === 1,
                    [styles.legendDotIntensa]: idx === 2,
                  })}
                />
                <span>{item.label} ({INTENSITY_RANGES[idx]})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AthleteBodyMap;
